import React from 'react';
import { useParams, Navigate, useLocation, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';
import StructuredData from '../components/StructuredData';
import NotFound from './NotFound';
import contentPages from '../generated/content-pages.json';
import { enHubSlugToOgSlug, getLocaleFromPathname, getSectionI18nKey, getSectionIndexPath } from '../config/localeRoutes';
import './ContentPage.css';

function ContentPage({ section, slug: slugProp }) {
  const { slug: slugParam } = useParams();
  const slug = slugProp || slugParam;
  const location = useLocation();
  const { t } = useTranslation();
  const page = contentPages[section]?.[slug];
  const locale = page?.locale || getLocaleFromPathname(location.pathname);

  const canonicalPath = page?.path
    || (section === 'hub' ? `/${slug}/` : `/${section}/${slug}/`);

  if (!location.pathname.endsWith('/')) {
    return <Navigate to={canonicalPath} replace />;
  }

  if (!page) {
    return <NotFound />;
  }

  const siteUrl = process.env.REACT_APP_SITE_URL || 'https://leagora.io';
  const ogSlug = section === 'hub-en' ? enHubSlugToOgSlug(slug) : slug;
  const ogSection = section === 'hub-en' ? 'hub' : section;
  const ogImage = `${siteUrl}/og/${ogSection}/${ogSlug}.png`;
  const sectionIndexPath = getSectionIndexPath(section);
  const sectionI18nKey = getSectionI18nKey(section);

  return (
    <>
      <SEO
        locale={locale}
        title={page.title}
        description={page.description}
        canonical={page.path}
        ogType={section === 'articles' ? 'article' : 'website'}
        ogImage={ogImage}
        articlePublishedTime={
          page.published ? `${page.published}T00:00:00+01:00` : null
        }
        articleModifiedTime={
          page.modified ? `${page.modified}T00:00:00+01:00` : null
        }
      />
      <StructuredData page="content" contentPage={page} locale={locale} />
      <main className="content-page" id="main-content">
        <div className="content-container">
          {sectionIndexPath && (
            <Link to={sectionIndexPath} className="content-back-link">
              ← {t(`${sectionI18nKey}.backToSection`)}
            </Link>
          )}
          <article
            className="content-body"
            dangerouslySetInnerHTML={{ __html: page.html }}
          />
        </div>
      </main>
    </>
  );
}

export default ContentPage;
